import { useEffect } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';

const CONTROL_VARIANT = 'control';
const TREATMENT_VARIANT = 'treatment';

export type AppleMapkitWebExperimentGate = {
  isReady: boolean;
  isTreatment: boolean;
  hasResolvedVariant: boolean;
  variantValue?: string;
};

type ResolveAppleMapkitWebExperimentGateArgs = {
  isExperimentLoading: boolean;
  variantValue?: string;
};

/**
 * Pure gate used by the hook and unit tests.
 * Ready when a control/treatment variant exists, or when Amplitude finished loading.
 * Safe default while unresolved: not treatment (Google Maps).
 */
export const resolveAppleMapkitWebExperimentGate = ({
  isExperimentLoading,
  variantValue,
}: ResolveAppleMapkitWebExperimentGateArgs): AppleMapkitWebExperimentGate => {
  const hasResolvedVariant = variantValue === CONTROL_VARIANT || variantValue === TREATMENT_VARIANT;
  const isReady = hasResolvedVariant || !isExperimentLoading;
  const isTreatment = variantValue === TREATMENT_VARIANT;

  return {
    isReady,
    isTreatment,
    hasResolvedVariant,
    variantValue,
  };
};

/**
 * Reads the Apple MapKit web experiment WITHOUT logging exposure.
 * Exposure is logged separately via {@link useExposeAppleMapkitWebExperiment}
 * when a web map is ready to initialize.
 */
export const useAppleMapkitWebExperiment = (): AppleMapkitWebExperimentGate => {
  const { getVariantValue, isLoading } = useExperiments();
  const variantValue = getVariantValue(RunningExperiments.EnableAppleMapkitWeb)?.value;

  return resolveAppleMapkitWebExperimentGate({
    isExperimentLoading: isLoading,
    variantValue,
  });
};

/**
 * Logs Amplitude exposure for control and treatment when a web map is eligible
 * to initialize. `logExposure` dedupes per variant, so multiple map mounts are safe.
 */
export const useExposeAppleMapkitWebExperiment = (shouldExpose: boolean) => {
  const { logExposure } = useExperiments();

  useEffect(() => {
    if (!shouldExpose) {
      return;
    }

    logExposure(RunningExperiments.EnableAppleMapkitWeb);
  }, [shouldExpose, logExposure]);
};
